import { z } from 'zod';
import type { Point2D, Polygon2D } from '@shared/models/geometry.model';
import { generateLShapedFootprintPolygon } from './l-shape-footprint.helpers';

/**
 * Generate a rectangle centered at origin.
 * @param width - Extent along x-axis (m)
 * @param depth - Extent along y-axis (m)
 */
export const generateRectangle = (width: number, depth: number): Polygon2D => {
  const hw = width / 2;
  const hd = depth / 2;
  return [
    [-hw, -hd],
    [hw, -hd],
    [hw, hd],
    [-hw, hd],
  ] as Polygon2D;
};

/**
 * Generate an L-shaped polygon. Cut is from the inner corner.
 * @param width - Total width (m)
 * @param depth - Total depth (m)
 * @param cutWidth - Width of the cut (m)
 * @param cutDepth - Depth of the cut (m)
 */
export const generateLShape = (
  width: number,
  depth: number,
  cutWidth: number,
  cutDepth: number,
): Polygon2D => {
  const hw = width / 2;
  const hd = depth / 2;
  return [
    [-hw, -hd],
    [hw, -hd],
    [hw, -hd + cutDepth],
    [-hw + cutWidth, -hd + cutDepth],
    [-hw + cutWidth, hd],
    [-hw, hd],
  ] as Polygon2D;
};

/**
 * Generate a U-shaped polygon. Cut is from the open side (bottom).
 * @param width - Total width (m)
 * @param depth - Total depth (m)
 * @param cutWidth - Width of the opening (m)
 * @param cutDepth - Depth of the cut (m)
 */
export const generateUShape = (
  width: number,
  depth: number,
  cutWidth: number,
  cutDepth: number,
): Polygon2D => {
  const hw = width / 2;
  const hd = depth / 2;
  const cutHalf = cutWidth / 2;
  return [
    [-hw, -hd],
    [-cutHalf, -hd],
    [-cutHalf, hd - cutDepth],
    [cutHalf, hd - cutDepth],
    [cutHalf, -hd],
    [hw, -hd],
    [hw, hd],
    [-hw, hd],
  ] as Polygon2D;
};

/**
 * Generate a T-shaped polygon. Horizontal bar on top, vertical stem below.
 * @param width - Total width of the top bar (m)
 * @param depth - Total depth (bar + stem) (m)
 * @param stemWidth - Width of the stem (m)
 * @param barDepth - Depth of the top bar (m)
 */
export const generateTShape = (
  width: number,
  depth: number,
  stemWidth: number,
  barDepth: number,
): Polygon2D => {
  const hw = width / 2;
  const stemHalf = stemWidth / 2;
  const stemDepth = depth - barDepth;
  return [
    [-stemHalf, -depth / 2],
    [stemHalf, -depth / 2],
    [stemHalf, -depth / 2 + stemDepth],
    [hw, -depth / 2 + stemDepth],
    [hw, depth / 2],
    [-hw, depth / 2],
    [-hw, -depth / 2 + stemDepth],
    [-stemHalf, -depth / 2 + stemDepth],
  ] as Polygon2D;
};

/**
 * Generate an H-shaped polygon. Two vertical bars of width {@link stemWidth}
 * connected by a horizontal bar of depth {@link barDepth} at the vertical
 * midpoint. Centered at origin.
 * @param width - Total width (m)
 * @param depth - Total depth (m)
 * @param stemWidth - Width of each vertical bar (m)
 * @param barDepth - Depth of the connecting horizontal bar (m)
 */
export const generateHShape = (
  width: number,
  depth: number,
  stemWidth: number,
  barDepth: number,
): Polygon2D => {
  const hw = width / 2;
  const hd = depth / 2;
  const hbd = barDepth / 2;
  const leftInner = -hw + stemWidth;
  const rightInner = hw - stemWidth;
  return [
    [-hw, -hd],
    [leftInner, -hd],
    [leftInner, -hbd],
    [rightInner, -hbd],
    [rightInner, -hd],
    [hw, -hd],
    [hw, hd],
    [rightInner, hd],
    [rightInner, hbd],
    [leftInner, hbd],
    [leftInner, hd],
    [-hw, hd],
  ] as Polygon2D;
};

// ---------------------------------------------------------------------------
// Building shape variants
//
// Five geometrically distinct footprint templates. Swedish archetype names
// are NOT separate variants — they are rendered by the same generators:
//   - rectangular covers: rectangular, punkthus (square), lamellhus,
//     skivhus, loftgångshus (long thin slab), and förskjutet lamellhus
//     (same rectangular lamell massing as a plain rectangle, but segmentCount /
//     segmentOffset build colinear volumes with lateral y-offsets so the outline
//     steps / notches in plan between segments).
//     Plain lamell archetypes only differ in proportions and storey count.
//   - l_shaped covers: l_shaped, vinkelhus / knäckt lamellhus (optional
//     segmentCount / segmentAngles: width = straight end-to-end span, depth =
//     strip thickness in plan; classic L keeps width/depth as outer box).
// The AI surface exposes one tool per variant; the tool descriptions tell
// the model which archetype keywords map to which variant and what typical
// dimensions look like.
// ---------------------------------------------------------------------------

/** Default outer dimension applied when a variant leaves a primary dim unset. */
export const DEFAULT_OUTER_M = 20;

/**
 * When {@link rectangularShapeParamsSchema} has segmentCount ≥ 2 and
 * segmentOffset is omitted, lateral (y) stagger between segment centres
 * defaults to this fraction of slab depth.
 */
export const DEFAULT_RECTANGULAR_SEGMENT_OFFSET_FRACTION = 0.5;

/** Keep lateral stagger inside strip depth so the slab union stays connected. */
const STAGGER_MAX_FRACTION_OF_DEPTH = 0.99;

const normUndirectedEdgeKey = (a: Point2D, b: Point2D): string => {
  const [x1, y1, x2, y2] =
    a[0] < b[0] || (a[0] === b[0] && a[1] <= b[1])
      ? [a[0], a[1], b[0], b[1]]
      : [b[0], b[1], a[0], a[1]];
  return `${x1},${y1},${x2},${y2}`;
};

const parseEdgeKey = (k: string): [Point2D, Point2D] => {
  const parts = k.split(',').map(Number);
  const x1 = parts[0]!;
  const y1 = parts[1]!;
  const x2 = parts[2]!;
  const y2 = parts[3]!;
  return [
    [x1, y1],
    [x2, y2],
  ];
};

/** How consecutive segment centres step sideways (y) for förskjuten lamell. */
export type RectangularSegmentStaggerPattern = 'alternating' | 'left' | 'right';

export const DEFAULT_RECTANGULAR_SEGMENT_STAGGER_PATTERN: RectangularSegmentStaggerPattern =
  'alternating';

/**
 * Lateral (y) centre offset for segment index i (0..n-1).
 *
 * - **alternating**: zigzag along the chain — centres at ±lateralStaggerM/2
 *   (even index −, odd +), so each step flips side; neighbour offset magnitude
 *   matches `lateralStaggerM` between consecutive centres.
 * - **left**: cumulative steps in +y — centres at 0, L, 2L, … (all offsets “one way”).
 * - **right**: cumulative steps in −y — 0, −L, −2L, …
 */
export const lateralCentreOffsetYForStaggeredLamell = (
  i: number,
  segmentCount: number,
  lateralStaggerM: number,
  pattern: RectangularSegmentStaggerPattern = DEFAULT_RECTANGULAR_SEGMENT_STAGGER_PATTERN,
): number => {
  const n = segmentCount;
  if (n < 2 || lateralStaggerM <= 0) return 0;
  if (pattern === 'left') {
    return i * lateralStaggerM;
  }
  if (pattern === 'right') {
    return -i * lateralStaggerM;
  }
  return (lateralStaggerM / 2) * (i % 2 === 0 ? -1 : 1);
};

/**
 * Union of axis-aligned rectangles → simple orthogonal polygon (CCW), then
 * translated so the bbox centre is at the origin.
 */
export const unionAxisAlignedRectsToPolygon = (
  rects: ReadonlyArray<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }>,
): Polygon2D => {
  if (rects.length === 0) {
    return generateRectangle(1, 1);
  }
  const xs = [...new Set(rects.flatMap((r) => [r.minX, r.maxX]))].sort(
    (a, b) => a - b,
  );
  const ys = [...new Set(rects.flatMap((r) => [r.minY, r.maxY]))].sort(
    (a, b) => a - b,
  );
  const nx = xs.length - 1;
  const ny = ys.length - 1;
  if (nx < 1 || ny < 1) {
    return generateRectangle(1, 1);
  }
  const occ: boolean[][] = Array.from({ length: nx }, () =>
    Array<boolean>(ny).fill(false),
  );
  for (let i = 0; i < nx; i++) {
    const xi0 = xs[i]!;
    const xi1 = xs[i + 1]!;
    for (let j = 0; j < ny; j++) {
      const yj0 = ys[j]!;
      const yj1 = ys[j + 1]!;
      const cx = (xi0 + xi1) / 2;
      const cy = (yj0 + yj1) / 2;
      const row = occ[i]!;
      row[j] = rects.some(
        (r) => cx >= r.minX && cx <= r.maxX && cy >= r.minY && cy <= r.maxY,
      );
    }
  }

  const edgeSet = new Set<string>();
  const toggle = (a: Point2D, b: Point2D): void => {
    const k = normUndirectedEdgeKey(a, b);
    if (edgeSet.has(k)) edgeSet.delete(k);
    else edgeSet.add(k);
  };

  for (let i = 0; i < nx; i++) {
    const row = occ[i]!;
    const x0 = xs[i]!;
    const x1 = xs[i + 1]!;
    for (let j = 0; j < ny; j++) {
      if (!row[j]) continue;
      const y0 = ys[j]!;
      const y1 = ys[j + 1]!;
      const west = i === 0 ? null : occ[i - 1]![j];
      const east = i === nx - 1 ? null : occ[i + 1]![j];
      const south = j === 0 ? null : row[j - 1];
      const north = j === ny - 1 ? null : row[j + 1];
      if (south == null || !south) {
        toggle([x0, y0], [x1, y0]);
      }
      if (north == null || !north) {
        toggle([x1, y1], [x0, y1]);
      }
      if (west == null || !west) {
        toggle([x0, y1], [x0, y0]);
      }
      if (east == null || !east) {
        toggle([x1, y0], [x1, y1]);
      }
    }
  }

  if (edgeSet.size === 0) {
    return generateRectangle(1, 1);
  }

  const neighbors = new Map<string, Point2D[]>();
  const addNeighbor = (a: Point2D, b: Point2D): void => {
    const ka = `${a[0]},${a[1]}`;
    const kb = `${b[0]},${b[1]}`;
    if (!neighbors.has(ka)) neighbors.set(ka, []);
    if (!neighbors.has(kb)) neighbors.set(kb, []);
    neighbors.get(ka)!.push(b);
    neighbors.get(kb)!.push(a);
  };
  for (const k of edgeSet) {
    const [a, b] = parseEdgeKey(k);
    addNeighbor(a, b);
  }

  const allKeys = [...neighbors.keys()];
  const parseKey = (key: string): Point2D =>
    key.split(',').map(Number) as unknown as Point2D;
  let start: Point2D = parseKey(allKeys[0]!);
  for (const key of allKeys) {
    const p = parseKey(key);
    if (p[1] < start[1] || (p[1] === start[1] && p[0] < start[0])) {
      start = p;
    }
  }

  let curr: Point2D = start;
  let prev: Point2D | null = null;
  const path: Point2D[] = [];

  for (let guard = 0; guard < edgeSet.size * 4 + 10; guard++) {
    path.push(curr);
    const nbrsHere = neighbors.get(`${curr[0]},${curr[1]}`)!;
    const candidates = nbrsHere.filter(
      (n) => prev == null || n[0] !== prev[0] || n[1] !== prev[1],
    );
    let nextPt: Point2D;
    if (prev == null) {
      const east = candidates.find((n) => n[1] === curr[1] && n[0] > curr[0]);
      nextPt = east ?? candidates[0]!;
    } else {
      nextPt = candidates[0]!;
    }
    if (nextPt[0] === start[0] && nextPt[1] === start[1] && path.length > 1) {
      break;
    }
    prev = curr;
    curr = nextPt;
  }

  const xsP = path.map((p) => p[0]);
  const ysP = path.map((p) => p[1]);
  const cx = (Math.min(...xsP) + Math.max(...xsP)) / 2;
  const cy = (Math.min(...ysP) + Math.max(...ysP)) / 2;
  const shifted = path.map(
    (p) => [p[0] - cx, p[1] - cy] as Point2D,
  ) as unknown as Polygon2D;
  return shifted;
};

/** Rectangular lamell as colinear segment boxes with lateral y-offsets (stepped / notched outline in plan). */
const generateStaggeredLamellFootprint = (
  slabWidth: number,
  depth: number,
  segmentCount: number,
  lateralStaggerM: number,
  staggerPattern: RectangularSegmentStaggerPattern,
): Polygon2D => {
  const n = Math.max(1, Math.floor(segmentCount));
  const w = slabWidth;
  const d = depth;
  if (n < 2) {
    return generateRectangle(w, d);
  }
  const lat = Math.max(0, lateralStaggerM);
  if (lat <= 0) {
    return generateRectangle(n * w, d);
  }
  const rects: Array<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }> = [];
  for (let i = 0; i < n; i++) {
    const cx = (i - (n - 1) / 2) * w;
    const cy = lateralCentreOffsetYForStaggeredLamell(
      i,
      n,
      lat,
      staggerPattern,
    );
    rects.push({
      minX: cx - w / 2,
      maxX: cx + w / 2,
      minY: cy - d / 2,
      maxY: cy + d / 2,
    });
  }
  return unionAxisAlignedRectsToPolygon(rects);
};

/** Upper bound (m) on any single footprint dimension passed to AI shape tools. */
export const MAX_DESIGN_SHAPE_DIMENSION_M = 5000;

const positiveMetersOptional = z
  .number()
  .positive()
  .max(MAX_DESIGN_SHAPE_DIMENSION_M)
  .nullable()
  .optional();

/** Lateral stagger may be 0 (flush colinear slabs); omit for default stagger. */
const nonnegSegmentOffsetMOptional = z
  .number()
  .min(0)
  .max(MAX_DESIGN_SHAPE_DIMENSION_M)
  .nullable()
  .optional();

const segmentStaggerPatternOptional = z
  .enum(['alternating', 'left', 'right'])
  .nullable()
  .optional()
  .describe(
    'For segmentCount ≥ 2: how sideways offsets step between segments along y. alternating = zigzag (±half segmentOffset per segment, flipping each step). left = cumulative offset in +y (0, L, 2L, …). right = cumulative in −y. Omit = alternating.',
  );

const segmentCountOptional = z
  .number()
  .int()
  .min(1)
  .max(16)
  .nullable()
  .optional()
  .describe(
    '1 = plain single rectangle. ≥2 = förskjuten lamell: same rectangular lamell as one box, but several colinear segments with lateral offsets (use segmentOffset); outline steps in plan. For ≥2, width is **total** chain length along x (end-to-end); each segment is width ÷ segmentCount along x.',
  );

export const rectangularShapeParamsSchema = z.object({
  width: positiveMetersOptional.describe(
    'Metres along the chain (x). One segment: full rectangle width. Förskjuten lamell (segmentCount ≥ 2): **total** end-to-end length of the lamell along the straight run — same overall length you would use for one continuous rectangle; segments only add sideways offsets between volumes.',
  ),
  depth: positiveMetersOptional.describe(
    'Lamell strip depth in metres (y-axis / "djup"), shared by every segment. May be omitted to preserve existing per-storey area when width is set.',
  ),
  segmentCount: segmentCountOptional,
  segmentOffset: nonnegSegmentOffsetMOptional.describe(
    'Sideways offset in metres **between** segment volumes along y (sidoförskjutning, e.g. 3 m), so the footprint outline steps instead of one flush rectangle. Use 0 for colinear segments with no lateral shift. Omit to default to half the slab depth. Values &gt; 0 are capped below slab depth so the union stays one connected mass.',
  ),
  segmentStaggerPattern: segmentStaggerPatternOptional,
});

/**
 * Resolved lateral (y) stagger between segment centres for förskjutet lamell.
 * For segmentCount &lt; 2, returns 0.
 */
export const resolveRectangularLateralStaggerM = (
  depth: number,
  segmentCount: number,
  segmentOffset: number | null | undefined,
): number => {
  const n = Math.max(1, Math.floor(segmentCount));
  if (n < 2 || depth <= 0) return 0;
  if (segmentOffset === 0) return 0;
  const raw =
    segmentOffset != null && segmentOffset > 0
      ? segmentOffset
      : depth * DEFAULT_RECTANGULAR_SEGMENT_OFFSET_FRACTION;
  return Math.min(raw, depth * STAGGER_MAX_FRACTION_OF_DEPTH);
};

/**
 * Total extent of the rectangular / staggered lamell footprint along x.
 * Matches the tool parameter `width`: full chain length for any segmentCount.
 */
export const getRectangularFootprintWidthMeters = (
  chainWidthM: number,
  _segmentCount: number | null | undefined,
  _segmentOffset: number | null | undefined,
): number => chainWidthM;

/**
 * Derive total chain extent along x from target footprint area and strip depth.
 * Union area is chainWidth × depth for colinear abutting slabs (any segment count).
 */
export const deriveRectangularSlabWidthFromArea = (
  targetArea: number,
  depth: number,
  _segmentCount: number | null | undefined,
  _segmentOffset: number | null | undefined,
): number => targetArea / depth;

const lShapeSegmentCountOptional = z
  .number()
  .int()
  .min(2)
  .max(16)
  .nullable()
  .optional()
  .describe(
    'Number of straight lamell segments in plan (minimum 2 for an L). Use 2 for a single bend; larger values chain bends for a gently arcing "knäckt lamellhus".',
  );

const lShapeSegmentAnglesOptional = z
  .array(z.number().min(90).max(179.99))
  .max(15)
  .nullable()
  .optional()
  .describe(
    'Interior angle in degrees at each bend between consecutive segments (length must be segmentCount − 1). 90° is a right-angle L; values toward 180° straighten the bend. Each angle must be ≥ 90° and &lt; 180°.',
  );

export const lShapeParamsSchema = z.object({
  width: positiveMetersOptional.describe(
    'Classic L: outer bounding-box width (m). Knäckt / multi-segment lamell: overall straight-line span end-to-end along the run (ändpunkt–ändpunkt), not developed outer-facade length.',
  ),
  depth: positiveMetersOptional.describe(
    'Classic L: outer bounding-box depth (m). Knäckt lamell: strip thickness in plan (typical lamell "djup", often ~10–14 m).',
  ),
  cutWidth: positiveMetersOptional.describe(
    'Classic L: width of the inner-corner notch (m). Defaults to width/3. Ignored for knäckt strip geometry (thickness comes from depth).',
  ),
  cutDepth: positiveMetersOptional.describe(
    'Classic L: depth of the inner-corner notch (m). Defaults to depth/3. Ignored for knäckt strip geometry.',
  ),
  segmentCount: lShapeSegmentCountOptional,
  segmentAngles: lShapeSegmentAnglesOptional,
});

export const uShapeParamsSchema = z.object({
  width: positiveMetersOptional.describe('Total width in metres.'),
  depth: positiveMetersOptional.describe('Total depth in metres.'),
  cutWidth: positiveMetersOptional.describe(
    'Width of the opening between the two wings in metres. Defaults to width/3.',
  ),
  cutDepth: positiveMetersOptional.describe(
    'How far the opening cuts into the footprint in metres. Defaults to depth/3.',
  ),
});

export const tShapeParamsSchema = z.object({
  width: positiveMetersOptional.describe(
    'Total width of the top bar in metres.',
  ),
  depth: positiveMetersOptional.describe('Total depth (bar + stem) in metres.'),
  stemWidth: positiveMetersOptional.describe(
    'Width of the vertical stem in metres. Defaults to width/3.',
  ),
  barDepth: positiveMetersOptional.describe(
    'Depth of the horizontal top bar in metres. Defaults to depth/3.',
  ),
});

export const hShapeParamsSchema = z.object({
  width: positiveMetersOptional.describe('Total width in metres.'),
  depth: positiveMetersOptional.describe('Total depth in metres.'),
  stemWidth: positiveMetersOptional.describe(
    'Width of each vertical bar in metres. Defaults to width/3.',
  ),
  barDepth: positiveMetersOptional.describe(
    'Depth of the horizontal connector in metres. Defaults to depth/3.',
  ),
});

/**
 * Tagged union of every building-shape variant accepted by the AI Designer
 * and other design tooling. Each variant exposes only the dimensions that
 * make sense for its shape.
 *
 * Kept as a zod union so {@link generateBuildingShape} and
 * {@link isBuildingShapeFullySized} can dispatch on `type`. The OpenAI tool
 * surface does NOT send this union directly — each archetype is registered
 * as its own function, see the per-variant `*ShapeParamsSchema` exports
 * above.
 */
export const buildingShapeVariantSchema = z.discriminatedUnion('type', [
  rectangularShapeParamsSchema.extend({ type: z.literal('rectangular') }),
  lShapeParamsSchema.extend({ type: z.literal('l_shaped') }),
  uShapeParamsSchema.extend({ type: z.literal('u_shaped') }),
  tShapeParamsSchema.extend({ type: z.literal('t_shaped') }),
  hShapeParamsSchema.extend({ type: z.literal('h_shaped') }),
]);

export type BuildingShapeVariant = z.infer<typeof buildingShapeVariantSchema>;
export type BuildingShapeVariantType = BuildingShapeVariant['type'];

/**
 * Clamp a detail dimension so it stays strictly inside its outer bound.
 * Cross-property invariants (e.g. `cutWidth < width`) cannot be enforced in
 * the per-variant JSON Schema the AI sees, and a value that hits or exceeds
 * the bound collapses or self-intersects the polygon. Leave a 1% sliver so
 * the shape always has visible area on either side of the cut.
 */
const DETAIL_MAX_FRACTION = 0.99;
const clampDetail = (value: number, outerBound: number): number =>
  Math.min(value, outerBound * DETAIL_MAX_FRACTION);

/**
 * Build a polygon from a {@link BuildingShapeVariant}. Missing dimensions
 * are filled with sensible defaults — callers that want to preserve a
 * previous GFA should rescale the result afterwards (see
 * {@link isBuildingShapeFullySized} for when that rescale should be skipped).
 *
 * Detail dimensions (cutWidth / cutDepth / stemWidth / barDepth) are clamped
 * to the outer bounds before being passed to the raw generators, so the AI
 * cannot produce a degenerate or self-intersecting footprint by submitting
 * e.g. `cutWidth >= width`.
 */
export const generateBuildingShape = (
  variant: BuildingShapeVariant,
): Polygon2D => {
  switch (variant.type) {
    case 'rectangular': {
      const chainW = variant.width ?? DEFAULT_OUTER_M;
      const depth = variant.depth ?? DEFAULT_OUTER_M;
      const n = Math.max(1, Math.floor(variant.segmentCount ?? 1));
      const slabW = n >= 2 ? chainW / n : chainW;
      const lat = resolveRectangularLateralStaggerM(
        depth,
        n,
        variant.segmentOffset,
      );
      const staggerPattern =
        variant.segmentStaggerPattern ??
        DEFAULT_RECTANGULAR_SEGMENT_STAGGER_PATTERN;
      return generateStaggeredLamellFootprint(
        slabW,
        depth,
        n,
        lat,
        staggerPattern,
      );
    }
    case 'l_shaped': {
      return generateLShapedFootprintPolygon(variant).polygon;
    }
    case 'u_shaped': {
      const width = variant.width ?? DEFAULT_OUTER_M;
      const depth = variant.depth ?? DEFAULT_OUTER_M;
      const cutWidth = clampDetail(variant.cutWidth ?? width / 3, width);
      const cutDepth = clampDetail(variant.cutDepth ?? depth / 3, depth);
      return generateUShape(width, depth, cutWidth, cutDepth);
    }
    case 't_shaped': {
      const width = variant.width ?? DEFAULT_OUTER_M;
      const depth = variant.depth ?? DEFAULT_OUTER_M;
      const stemWidth = clampDetail(variant.stemWidth ?? width / 3, width);
      const barDepth = clampDetail(variant.barDepth ?? depth / 3, depth);
      return generateTShape(width, depth, stemWidth, barDepth);
    }
    case 'h_shaped': {
      const width = variant.width ?? DEFAULT_OUTER_M;
      const depth = variant.depth ?? DEFAULT_OUTER_M;
      // Two stems of stemWidth sit at the left/right edges and must not meet
      // in the middle, so each stem is bounded by width/2, not width.
      const stemWidth = clampDetail(variant.stemWidth ?? width / 3, width / 2);
      const barDepth = clampDetail(variant.barDepth ?? depth / 3, depth);
      return generateHShape(width, depth, stemWidth, barDepth);
    }
  }
};

/**
 * True when the variant fully specifies its outer size (all primary outer
 * dims are given). When true, callers should honour the specified
 * dimensions and NOT rescale the result to preserve a previous GFA.
 * Detail dims (cutWidth/cutDepth/stemWidth/barDepth) do not count.
 */
export const isBuildingShapeFullySized = (
  variant: BuildingShapeVariant,
): boolean => {
  switch (variant.type) {
    case 'rectangular':
    case 'l_shaped':
    case 'u_shaped':
    case 't_shaped':
    case 'h_shaped':
      return variant.width != null && variant.depth != null;
  }
};
